import React, { useState, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../context/AuthContext';
import { GripVertical, Eye, EyeOff, RotateCcw } from 'lucide-react';
import {
  LayoutDashboard, Users, UserPlus, Receipt, Ticket,
  FlaskConical, CalendarDays, ActivitySquare, Stethoscope,
  Pill, Settings, DatabaseBackup, ShieldCheck, Wallet,
} from 'lucide-react';

const ICON_MAP = {
  LayoutDashboard, Users, UserPlus, Receipt, Ticket,
  FlaskConical, CalendarDays, ActivitySquare, Stethoscope,
  Pill, Settings, DatabaseBackup, ShieldCheck, Wallet,
};

const DEFAULT_DOCTOR_NAV = [
  { key: 'dashboard',     label: 'Dashboard',         icon: 'LayoutDashboard', to: '/doctor' },
  { key: 'create-token',  label: 'Create Token',      icon: 'Ticket',          to: '/doctor/create-token' },
  { key: 'queue',         label: 'Patient Queue',     icon: 'Users',           to: '/doctor/queue' },
  { key: 'patients',      label: 'All Patients',      icon: 'UserPlus',        to: '/doctor/patients' },
  { key: 'tracker',       label: 'Patient Tracker',   icon: 'ActivitySquare',  to: '/doctor/tracker' },
  { key: 'billing',       label: 'Billing',           icon: 'Receipt',         to: '/doctor/billing' },
  { key: 'expenses',      label: 'Expenses',          icon: 'Wallet',          to: '/doctor/expenses' },
  { key: 'lab',           label: 'LAB',               icon: 'FlaskConical',    to: '/doctor/lab' },
  { key: 'pharmacy',      label: 'Pharmacy',          icon: 'Pill',            to: '/doctor/pharmacy' },
  { key: 'treatments',    label: 'Treatment Catalog', icon: 'Stethoscope',     to: '/doctor/treatments' },
  { key: 'calendar',      label: 'Calendar',          icon: 'CalendarDays',    to: '/doctor/calendar' },
  { key: 'sterilisation', label: 'Sterilisation Log', icon: 'ShieldCheck',     to: '/doctor/sterilisation' },
  { key: 'settings',      label: 'Account Settings',  icon: 'Settings',        to: '/doctor/settings' },
  { key: 'backup',        label: 'Backup & Restore',  icon: 'DatabaseBackup',  to: '/doctor/backup' },
];

const DEFAULT_RECEPTIONIST_NAV = [
  { key: 'home',          label: 'Create Token',      icon: 'UserPlus',        to: '/receptionist' },
  { key: 'queue',         label: "Today's Queue",     icon: 'Users',           to: '/receptionist/queue' },
  { key: 'patients',      label: 'All Patients',      icon: 'LayoutDashboard', to: '/receptionist/patients' },
  { key: 'tracker',       label: 'Patient Tracker',   icon: 'ActivitySquare',  to: '/receptionist/tracker' },
  { key: 'billing',       label: 'Billing',           icon: 'Receipt',         to: '/receptionist/billing' },
  { key: 'expenses',      label: 'Expenses',          icon: 'Wallet',          to: '/receptionist/expenses' },
  { key: 'lab',           label: 'LAB',               icon: 'FlaskConical',    to: '/receptionist/lab' },
  { key: 'pharmacy',      label: 'Pharmacy',          icon: 'Pill',            to: '/receptionist/pharmacy' },
  { key: 'treatments',    label: 'Treatment Catalog', icon: 'Stethoscope',     to: '/receptionist/treatments' },
  { key: 'calendar',      label: 'Calendar',          icon: 'CalendarDays',    to: '/receptionist/calendar' },
  { key: 'sterilisation', label: 'Sterilisation Log', icon: 'ShieldCheck',     to: '/receptionist/sterilisation' },
  { key: 'settings',      label: 'Account Settings',  icon: 'Settings',        to: '/receptionist/settings' },
];

export function getNavItems(role) {
  const key   = role === 'doctor' ? 'nav_order_doctor' : 'nav_order_receptionist';
  const defaults = role === 'doctor' ? DEFAULT_DOCTOR_NAV : DEFAULT_RECEPTIONIST_NAV;
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return defaults;
    const parsed = JSON.parse(stored);
    // Merge: keep stored order/hidden, add any new defaults not in stored
    const storedKeys = new Set(parsed.map(i => i.key));
    const merged = [...parsed, ...defaults.filter(d => !storedKeys.has(d.key))];
    return merged;
  } catch { return defaults; }
}

function saveNavItems(role, items) {
  const key = role === 'doctor' ? 'nav_order_doctor' : 'nav_order_receptionist';
  localStorage.setItem(key, JSON.stringify(items));
  // Dispatch storage event so AppLayout re-reads immediately
  window.dispatchEvent(new StorageEvent('storage', { key }));
}

export default function NavSettingsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(() => getNavItems(user?.role));
  const [saved, setSaved] = useState(false);
  const dragIdx  = useRef(null);
  const dragOver = useRef(null);

  // Items that cannot be hidden (core navigation)
  const REQUIRED = new Set(['dashboard','create-token','home','settings']);

  function onDragStart(i) { dragIdx.current = i; }
  function onDragEnter(i) { dragOver.current = i; }
  function onDragEnd() {
    if (dragIdx.current === null || dragOver.current === null || dragIdx.current === dragOver.current) {
      dragIdx.current = dragOver.current = null; return;
    }
    const arr = [...items];
    const [moved] = arr.splice(dragIdx.current, 1);
    arr.splice(dragOver.current, 0, moved);
    setItems(arr);
    dragIdx.current = dragOver.current = null;
    setSaved(false);
  }

  function toggleVisible(key) {
    setItems(prev => prev.map(it => it.key === key ? { ...it, hidden: !it.hidden } : it));
    setSaved(false);
  }

  function handleSave() {
    saveNavItems(user?.role, items);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function handleReset() {
    const defaults = user?.role === 'doctor' ? DEFAULT_DOCTOR_NAV : DEFAULT_RECEPTIONIST_NAV;
    setItems(defaults.map(d => ({ ...d })));
    setSaved(false);
  }

  const visible = items.filter(i => !i.hidden);
  const hidden  = items.filter(i => i.hidden);

  return (
    <AppLayout title="Menu Settings" subtitle="Reorder and show/hide sidebar navigation items">
      <div style={{ maxWidth: 560 }}>

        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div>
              <div style={{ fontWeight:700, fontSize:15 }}>Menu Order</div>
              <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2 }}>
                Drag items to reorder · Click eye to show/hide
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-outline btn-sm" onClick={handleReset} title="Reset to default order">
                <RotateCcw size={13} /> Reset
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleSave}>
                {saved ? '✓ Saved!' : 'Save Menu'}
              </button>
            </div>
          </div>

          {/* Visible items */}
          <div style={{ fontSize:11, fontWeight:700, color:'var(--color-ink-faint)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>
            Visible ({visible.length})
          </div>
          <div style={{ display:'grid', gap:4, marginBottom:20 }}>
            {items.map((item, i) => {
              if (item.hidden) return null;
              const Icon = ICON_MAP[item.icon] || Settings;
              return (
                <div key={item.key}
                  draggable
                  onDragStart={() => onDragStart(i)}
                  onDragEnter={() => onDragEnter(i)}
                  onDragEnd={onDragEnd}
                  onDragOver={e => e.preventDefault()}
                  style={{
                    display:'flex', alignItems:'center', gap:10,
                    padding:'10px 12px', borderRadius:8,
                    border:'1px solid var(--color-border)',
                    background:'var(--color-surface)',
                    cursor:'grab', userSelect:'none',
                  }}>
                  <GripVertical size={14} color="var(--color-ink-faint)" style={{ flexShrink:0 }} />
                  <Icon size={15} color="var(--color-primary)" style={{ flexShrink:0 }} />
                  <span style={{ flex:1, fontSize:13, fontWeight:600 }}>{item.label}</span>
                  <span style={{ fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>#{i+1}</span>
                  {!REQUIRED.has(item.key) && (
                    <button type="button" onClick={() => toggleVisible(item.key)}
                      style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:'var(--color-ink-soft)', display:'flex', alignItems:'center' }}
                      title="Hide from menu">
                      <Eye size={15} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Hidden items */}
          {hidden.length > 0 && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--color-ink-faint)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>
                Hidden ({hidden.length}) — click eye to restore
              </div>
              <div style={{ display:'grid', gap:4 }}>
                {hidden.map((item) => {
                  const Icon = ICON_MAP[item.icon] || Settings;
                  return (
                    <div key={item.key}
                      style={{
                        display:'flex', alignItems:'center', gap:10,
                        padding:'10px 12px', borderRadius:8,
                        border:'1px dashed var(--color-border)',
                        background:'var(--color-surface)',
                        opacity:0.6,
                      }}>
                      <Icon size={15} color="var(--color-ink-faint)" style={{ flexShrink:0 }} />
                      <span style={{ flex:1, fontSize:13, color:'var(--color-ink-faint)' }}>{item.label}</span>
                      <button type="button" onClick={() => toggleVisible(item.key)}
                        style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:'var(--color-primary)', display:'flex', alignItems:'center' }}
                        title="Show in menu">
                        <EyeOff size={15} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="card" style={{ padding:14, fontSize:12, color:'var(--color-ink-faint)', lineHeight:1.6 }}>
          <strong>Tips:</strong> Drag the ⠿ handle to reorder · Click 👁 to hide an item · Hidden items remain accessible by restoring them here · Dashboard and Settings cannot be hidden
        </div>
      </div>
    </AppLayout>
  );
}
